import { apiClient } from '../../core/api';

export type ContentDraftStatus = 'DRAFT' | 'APPROVED' | 'PUBLISHED' | 'DISCARDED';

export interface ContentDraft {
  id: string;
  /** Agent CREATE_CONTENT_DRAFT draft_id — matches a persisted draft to its
   *  in-chat card (used to restore a discarded card after navigating away). */
  clientDraftId?: string | null;
  conversationId?: string | null;
  contentType: string;
  /** Target channel (shopify/instagram/facebook) — drives the format badge. */
  platform?: string | null;
  title?: string | null;
  body?: string | null;
  productsSnapshot?: string | null;
  campaignSnapshot?: string | null;
  /** Durable image handles (JSON string): {"items":[{"image_key","role"}]}. */
  media?: string | null;
  /** The draft's own cover, already resolved to a loadable URL by chatbot-api (null when it has
   *  none). The browser cannot derive this itself: an Image Studio key is tenant-private and
   *  needs a presigned URL. Same resolver the Material Hub uses, so one draft cannot show two
   *  different pictures depending on the page. */
  imageUrl?: string | null;
  status: ContentDraftStatus;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const basePath = (userId: string) => `/digital-staff/content-drafts/${encodeURIComponent(userId)}`;

/**
 * The merchant's drafts, newest first.
 *
 * `limit` bounds the read. Unbounded is the default and stays right for the callers that
 * need the whole history (resolving a backend id for a discard), but it is the wrong shape
 * for the chat page, which re-reads this list after every turn to pick up a picture attached
 * AFTER the draft was filed. On dev one merchant already holds 420 drafts carrying 1.07 MB of
 * body text; over the gateway that response took more than 25 seconds, the refresh timed out,
 * and the card silently kept no picture. The server ignores a limit it cannot parse, so an
 * older backend simply answers with everything.
 */
export interface ListContentDraftsOptions {
  /** Newest N rows; absent = the merchant's whole history. */
  limit?: number;
  /** Only the drafts filed from this conversation — what the chat page's cards need. */
  conversationId?: string | null;
  /** `light` = rows without body / productsSnapshot / campaignSnapshot: ids, channel, status,
   *  media and picture only. Enough for the picture-by-title and discarded maps, and a fraction
   *  of the bytes (dev 2026-09-05: the 120-row full window was 417 KB). */
  fields?: 'light';
  /** Per-call timeout. The client default is 10 s; these lists take longer than that through
   *  the dev gateway under load, and a list that times out leaves cards without pictures. */
  timeoutMs?: number;
}

/** How long a draft-list read may take before the caller gives up (the client default is 10 s). */
export const DRAFT_LIST_TIMEOUT_MS = 60_000;

export async function listContentDrafts(
  userId: string,
  options?: ListContentDraftsOptions
): Promise<ContentDraft[]> {
  const params = new URLSearchParams();
  if (typeof options?.limit === 'number' && options.limit > 0)
    params.set('limit', String(options.limit));
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.fields) params.set('fields', options.fields);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiClient.chatbotGet<ContentDraft[]>(
    `${basePath(userId)}${query}`,
    undefined,
    options?.timeoutMs ? { timeout: options.timeoutMs } : undefined
  );
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * The rows the chat page draws its cards from: this conversation's drafts in full (their bodies
 * are the cards' text and their pictures the cards' covers), then the newest window of the
 * merchant's history without text (for the picture-by-title and discarded maps). Two small reads
 * instead of one large one — measured on dev 2026-09-05 the single 120-row full read took 39 s
 * under load and the browser gave up at 10 s, so cards whose picture the server had already
 * attached stayed imageless until a reload. Either read failing leaves the other's rows; the
 * conversation's own rows come first so the maps' "first row wins" keeps preferring them.
 */
export async function listDraftRowsForChat(
  userId: string,
  conversationId: string | null | undefined,
  window: number
): Promise<ContentDraft[]> {
  const reads: Promise<ContentDraft[]>[] = [
    listContentDrafts(userId, { limit: window, fields: 'light', timeoutMs: DRAFT_LIST_TIMEOUT_MS }),
  ];
  if (conversationId) {
    reads.unshift(listContentDrafts(userId, { conversationId, timeoutMs: DRAFT_LIST_TIMEOUT_MS }));
  }
  const settled = await Promise.allSettled(reads);
  const rows: ContentDraft[] = [];
  const seen = new Set<string>();
  for (const r of settled) {
    if (r.status !== 'fulfilled') continue;
    for (const row of r.value) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
  }
  if (settled.every(r => r.status === 'rejected')) {
    throw (settled[0] as PromiseRejectedResult).reason;
  }
  return rows;
}

export interface CreateContentDraftPayload {
  contentType: string; // blog | social | campaign
  /** Target channel (shopify/instagram/facebook) for the format badge. */
  platform?: string | null;
  conversationId?: string | null;
  /** Idempotency key (agent draft_id); create upserts on (userId, this). */
  clientDraftId?: string | null;
  title?: string;
  body?: string;
  productsSnapshot?: string; // JSON string
  campaignSnapshot?: string; // JSON string
  media?: string; // JSON string: {"items":[{"image_key","role"}]}
}

export async function createContentDraft(
  userId: string,
  payload: CreateContentDraftPayload
): Promise<ContentDraft> {
  const response = await apiClient.chatbotPost<ContentDraft>(basePath(userId), payload);
  return response.data;
}

export async function updateContentDraft(
  userId: string,
  draftId: string,
  payload: { title?: string; body?: string }
): Promise<ContentDraft> {
  const response = await apiClient.chatbotPut<ContentDraft>(
    `${basePath(userId)}/${encodeURIComponent(draftId)}`,
    payload
  );
  return response.data;
}

export async function approveContentDraft(userId: string, draftId: string): Promise<ContentDraft> {
  const response = await apiClient.chatbotPost<ContentDraft>(
    `${basePath(userId)}/${encodeURIComponent(draftId)}/approve`,
    {}
  );
  return response.data;
}

export async function discardContentDraft(userId: string, draftId: string): Promise<ContentDraft> {
  const response = await apiClient.chatbotPost<ContentDraft>(
    `${basePath(userId)}/${encodeURIComponent(draftId)}/discard`,
    {}
  );
  return response.data;
}
